import api from './api';

const appendListParam = (params, key, values) => {
  if (Array.isArray(values) && values.length > 0) {
    values.forEach((value) => {
      if (value) params.append(key, value);
    });
  } else if (typeof values === 'string' && values) {
    params.append(key, values);
  }
};

export const getTickets = async (
  page = '',
  status = [],
  priority = [],
  size = '',
  appOrigin = [],
  filters = {},
  requestConfig = {}
) => {
  try {
    const { signal } = requestConfig;
    const {
      demandType = [],
      companyName = [],
      responsibleUserEmail = [],
      searchText = '',
    } = filters;
    const params = new URLSearchParams();

    if (page !== '' && page !== undefined && page !== null) {
      params.append('page', page);
    }

    if (size !== '' && size !== undefined && size !== null) {
      params.append('size', size);
    }

    appendListParam(params, 'status', status);
    appendListParam(params, 'priority', priority);
    appendListParam(params, 'appOrigin', appOrigin);
    appendListParam(params, 'demandType', demandType);
    appendListParam(params, 'companyName', companyName);
    appendListParam(params, 'responsibleUserEmail', responsibleUserEmail);

    if (searchText && String(searchText).trim()) {
      params.append('searchText', String(searchText).trim());
    }

    const response = await api.get('/api/private/ticket', { params, signal });
    return response.data;
  } catch (error) {
    console.error('Error fetching tickets:', error);
    throw error;
  }
};

export const getTicketsByResponsibleUser = async (
  userInput,
  page = '',
  size = '',
  status = [],
  priority = [],
  appOrigin = [],
  requestConfig = {}
) => {
  try {
    const { signal } = requestConfig;
    const params = new URLSearchParams();

    if (userInput) {
      params.append('userInput', userInput);
    }

    if (page !== '' && page !== undefined && page !== null) {
      params.append('page', page);
    }

    if (size !== '' && size !== undefined && size !== null) {
      params.append('size', size);
    }

    if (Array.isArray(status) && status.length > 0) {
      status.forEach((statusValue) => {
        if (statusValue) {
          params.append('status', statusValue);
        }
      });
    } else if (typeof status === 'string' && status) {
      params.append('status', status);
    }

    if (Array.isArray(priority) && priority.length > 0) {
      priority.forEach((priorityValue) => {
        if (priorityValue) {
          params.append('priority', priorityValue);
        }
      });
    } else if (typeof priority === 'string' && priority) {
      params.append('priority', priority);
    }

    if (Array.isArray(appOrigin) && appOrigin.length > 0) {
      appOrigin.forEach((originValue) => {
        if (originValue) {
          params.append('appOrigin', originValue);
        }
      });
    } else if (typeof appOrigin === 'string' && appOrigin) {
      params.append('appOrigin', appOrigin);
    }

    const response = await api.get('/api/private/ticket/byUser', { params, signal });
    return response.data;
  } catch (error) {
    console.error('Error fetching tickets by responsible user:', error);
    throw error;
  }
};

export const getTicketById = async (ticketId) => {
  try {
    const response = await api.get(`/api/private/ticket/${ticketId}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching ticket ${ticketId}:`, error);
    throw error;
  }
};

/**
 * Retorna a contagem de tickets em aberto por appOrigin.
 * count é o total aberto; bugCount é SUPPORT e otherCount é o restante.
 * Resposta esperada: [{ appOrigin: "TAPINOUT", count: 3, bugCount: 2, otherCount: 1 }, ...]
 */
export const getOpenCountByApp = async () => {
  try {
    const response = await api.get('/api/private/ticket/open-count-by-app');
    return response.data;
  } catch (error) {
    console.error('Error fetching open count by app:', error);
    throw error;
  }
};

/**
 * Retorna a contagem de tickets resolvidos/fechados por usuário responsável.
 * Resposta esperada: [{ responsibleUserEmail: "user@example.com", count: 5 }, ...]
 */
export const getResolvedClosedByResponsibleOverTime = async (filters = {}) => {
  const {
    from,
    to,
    bucket = 'MONTH',
    demandType = [],
    responsibleUserName = [],
  } = filters;
  try {
    const params = new URLSearchParams();
    if (from) params.append('from', from);
    if (to) params.append('to', to);
    if (bucket) params.append('bucket', bucket);
    appendListParam(params, 'demandType', demandType);
    appendListParam(params, 'responsibleUserName', responsibleUserName);
    const qs = params.toString();
    const path = `/api/private/ticket/resolved-closed-by-responsible-over-time${qs ? `?${qs}` : ''}`;
    const response = await api.get(path);
    return response.data;
  } catch (error) {
    console.error('Error fetching responsible performance over time:', error);
    throw error;
  }
};

export const getSupportResolutionBreakdown = async (filters = {}) => {
  const { from, to } = filters;
  try {
    const params = new URLSearchParams();
    if (from) params.append('from', from);
    if (to) params.append('to', to);
    const qs = params.toString();
    const path = `/api/private/ticket/support-resolution-breakdown${qs ? `?${qs}` : ''}`;
    const response = await api.get(path);
    return response.data;
  } catch (error) {
    console.error('Error fetching support resolution breakdown:', error);
    throw error;
  }
};

export const getResolvedClosedCountByResponsibleUser = async (filters = {}) => {
  const { demandType = [], from, to } = filters;
  try {
    const params = new URLSearchParams();
    appendListParam(params, 'demandType', demandType);
    if (from) params.append('from', from);
    if (to) params.append('to', to);
    const qs = params.toString();
    const path = `/api/private/ticket/resolved-closed-by-responsible${qs ? `?${qs}` : ''}`;
    const response = await api.get(path);
    return response.data;
  } catch (error) {
    console.error('Error fetching resolved/closed count by responsible user:', error);
    throw error;
  }
};

/**
 * Retorna a quantidade total de tickets por aplicativo.
 * Resposta esperada: [{ appOrigin: "TAPINOUT", count: 10 }, ...]
 * @param {{ status?: string[], priority?: string[], appOrigin?: string[] }} [filters]
 */
export const getTotalByApp = async (filters = {}) => {
  const { status = [], priority = [], appOrigin = [], demandType = [] } = filters;
  try {
    const params = new URLSearchParams();
    appendListParam(params, 'status', status);
    appendListParam(params, 'priority', priority);
    appendListParam(params, 'appOrigin', appOrigin);
    appendListParam(params, 'demandType', demandType);
    const qs = params.toString();
    const path = `/api/private/ticket/total-by-app${qs ? `?${qs}` : ''}`;
    const response = await api.get(path);
    return response.data;
  } catch (error) {
    console.error('Error fetching total by app:', error);
    throw error;
  }
};

/**
 * Total de tickets por aplicação agrupado por mês (creationDate), alinhado ao backend.
 * GET /api/private/ticket/total-by-app-by-month
 * @param {{ from: string, to: string, status?: string[], priority?: string[], appOrigin?: string[] }} params — from/to em YYYY-MM-DD (LocalDate)
 */
export const getTotalByAppByMonth = async (params = {}) => {
  const { from, to, status = [], priority = [], appOrigin = [], demandType = [] } = params;
  if (!from || !to) {
    throw new Error('getTotalByAppByMonth: from e to são obrigatórios (YYYY-MM-DD).');
  }
  try {
    const search = new URLSearchParams();
    search.append('from', from);
    search.append('to', to);
    appendListParam(search, 'status', status);
    appendListParam(search, 'priority', priority);
    appendListParam(search, 'appOrigin', appOrigin);
    appendListParam(search, 'demandType', demandType);
    const qs = search.toString();
    const path = `/api/private/ticket/total-by-app-by-month${qs ? `?${qs}` : ''}`;
    const response = await api.get(path);
    return response.data;
  } catch (error) {
    console.error('Error fetching total by app by month:', error);
    throw error;
  }
};

/**
 * Retorna a quantidade total de tickets por usuário criador, com quebra por demanda.
 * Resposta esperada:
 * [{ responsibleUserName: "Ana", count: 13, byDemandType: [{ demandType: "SUPPORT", count: 10 }, ...] }, ...]
 */
export const getTotalCreatedByUser = async (filters = {}) => {
  const { demandType = [] } = filters;
  try {
    const params = new URLSearchParams();
    appendListParam(params, 'demandType', demandType);
    const qs = params.toString();
    const path = `/api/private/ticket/total-by-created-user${qs ? `?${qs}` : ''}`;
    const response = await api.get(path);
    return response.data;
  } catch (error) {
    console.error('Error fetching total created by user:', error);
    throw error;
  }
};

/**
 * Retorna os 5 últimos tickets criados.
 * GET /api/private/ticket/latest
 */
export const getLatestTickets = async (size = 5) => {
  try {
    const params = new URLSearchParams();
    if (size != null && size !== '') {
      params.append('size', size);
    }
    const response = await api.get('/api/private/ticket/latest', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching latest tickets:', error);
    throw error;
  }
};

export const getTicketImages = async (ticketId) => {
  try {
    const response = await api.get(`/api/private/ticket/image/${ticketId}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching images for ticket ${ticketId}:`, error);
    throw error;
  }
};

export const uploadTicketImage = async (ticketId, file) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(
      `/api/private/ticket/${ticketId}/image`,
      formData,
      { timeout: 180000 }
    );
    return response.data;
  } catch (error) {
    console.error(`Error uploading image for ticket ${ticketId}:`, error);
    throw error;
  }
};

export const getTicketPdfs = async (ticketId) => {
  try {
    const response = await api.get(`/api/private/ticket/pdf/${ticketId}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching PDFs for ticket ${ticketId}:`, error);
    throw error;
  }
};

export const uploadTicketPdf = async (ticketId, file) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(
      `/api/private/ticket/${ticketId}/pdf`,
      formData,
      { timeout: 180000 }
    );
    return response.data;
  } catch (error) {
    console.error(`Error uploading PDF for ticket ${ticketId}:`, error);
    throw error;
  }
};

export const getTicketVideos = async (ticketId) => {
  try {
    const response = await api.get(`/api/private/ticket/video/${ticketId}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching videos for ticket ${ticketId}:`, error);
    throw error;
  }
};

export const uploadTicketVideo = async (ticketId, file) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(
      `/api/private/ticket/${ticketId}/video`,
      formData,
      { timeout: 180000 }
    );
    return response.data;
  } catch (error) {
    console.error(`Error uploading video for ticket ${ticketId}:`, error);
    throw error;
  }
};

export const getTicketAudios = async (ticketId) => {
  try {
    const response = await api.get(`/api/private/ticket/audio/${ticketId}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching audios for ticket ${ticketId}:`, error);
    throw error;
  }
};

export const uploadTicketAudio = async (ticketId, file) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(
      `/api/private/ticket/${ticketId}/audio`,
      formData,
      { timeout: 180000 }
    );
    return response.data;
  } catch (error) {
    console.error(`Error uploading audio for ticket ${ticketId}:`, error);
    throw error;
  }
};

export const createTicket = async (ticketData) => {
  try {
    const response = await api.post('/api/private/ticket', ticketData);
    return response.data;
  } catch (error) {
    console.error('Error creating ticket:', error);
    throw error;
  }
};

export const updateTicket = async (ticketId, ticketData) => {
  try {
    const response = await api.put(`/api/private/ticket/${ticketId}`, ticketData);
    return response.data;
  } catch (error) {
    console.error(`Error updating ticket ${ticketId}:`, error);
    throw error;
  }
};

export const deleteTicket = async (ticketId) => {
  try {
    await api.delete(`/api/private/ticket/${ticketId}`);
  } catch (error) {
    console.error(`Error deleting ticket ${ticketId}:`, error);
    throw error;
  }
};

export { updateTicket as update, deleteTicket as delete };

export const addComment = async (ticketId, commentData) => {
  try {
    const response = await api.post(`/api/private/ticket/${ticketId}/comments`, commentData);
    return response.data;
  } catch (error) {
    console.error(`Error adding comment to ticket ${ticketId}:`, error);
    throw error;
  }
};

export const closeTicket = async (ticketId) => {
  try {
    const response = await api.put(`/api/private/ticket/close/${ticketId}`);
    return response.data;
  } catch (error) {
    console.error(`Erro ao fechar o ticket ${ticketId}:`, error);
    throw error;
  }
};

export const inProgressTicket = async (ticketId) => {
  try {
    const response = await api.put(`/api/private/ticket/in_progress/${ticketId}`);
    return response.data;
  } catch (error) {
    console.error(`Erro ao marcar o ticket ${ticketId} como em progresso:`, error);
    throw error;
  }
};

export const resolvedTicket = async (ticketId) => {
  try {
    const response = await api.put(`/api/private/ticket/resolved/${ticketId}`);
    return response.data;
  } catch (error) {
    console.error(`Erro ao marcar o ticket ${ticketId} como resolvido:`, error);
  }
};

export const openTicket = async (ticketId) => {
  try {
    const response = await api.put(`/api/private/ticket/open/${ticketId}`);
    return response.data;
  } catch (error) {
    console.error(`Erro ao marcar o ticket ${ticketId} como aberto:`, error);
  }
};

export const pendingTicket = async (ticketId) => {
  try {
    const response = await api.put(`/api/private/ticket/pending/${ticketId}`);
    return response.data;
  } catch (error) {
    console.error(`Erro ao marcar o ticket ${ticketId} como pendente:`, error);
  }
}

export const highTicket = async (ticketId) => {
  try {
    const response = await api.put(`/api/private/ticket/high/${ticketId}`);
    return response.data;
  } catch (error) {
    console.error(`Erro ao marcar a prioridade do ticket ${ticketId} como alta:`, error);
  }
};

export const criticalTicket = async (ticketId) => {
  try {
    const response = await api.put(`/api/private/ticket/critical/${ticketId}`);
    return response.data;
  } catch (error) {
    console.error(`Erro ao marcar a prioridade do ticket ${ticketId} como crítica:`, error);
  }
};

export const lowTicket = async (ticketId) => {
  try {
    const response = await api.put(`/api/private/ticket/low/${ticketId}`);
    return response.data;
  } catch (error) {
    console.error(`Erro ao marcar a prioridade do ticket ${ticketId} como baixa:`, error);
  }
};

export const mediumTicket = async (ticketId) => {
  try {
    const response = await api.put(`/api/private/ticket/medium/${ticketId}`);
    return response.data;
  } catch (error) {
    console.error(`Erro ao marcar a prioridade do ticket ${ticketId} como média:`, error);
  }
};

export const updateResponsibleUser = async (ticketId, responsibleUser) => {
  try {
    const response = await api.put(`/api/private/ticket/responsible/${ticketId}`, {
      responsibleUser: responsibleUser
    });
    return response.data;
  } catch (error) {
    console.error(`Erro ao atualizar o responsável do ticket ${ticketId}:`, error);
    throw error;
  }
};

/**
 * Atualiza a lista de responsáveis do ticket.
 * @param {string|number} ticketId
 * @param {Array} responsibleUsers - Lista de objetos usuário (ex.: [{ id, name, email }, ...])
 */
export const updateResponsibleUsers = async (ticketId, responsibleUsers) => {
  try {
    const response = await api.put(`/api/private/ticket/responsible/${ticketId}`, {
      responsibleUsers: Array.isArray(responsibleUsers) ? responsibleUsers : []
    });
    return response.data;
  } catch (error) {
    console.error(`Erro ao atualizar responsáveis do ticket ${ticketId}:`, error);
    throw error;
  }
};

export const updateTicketDetails = async (ticketId, ticketData) => {
  try {
    const response = await api.put(`/api/private/ticket/update-details/${ticketId}`, ticketData);
    return response.data;
  } catch (error) {
    console.error(`Error updating ticket details for ${ticketId}:`, error);
    throw error;
  }
};

export const updateTicketStatus = async (ticketId, status) => {
  try {
    const response = await api.patch(`/api/private/ticket/${ticketId}/status`, { status });
    return response.data;
  } catch (error) {
    console.error(`Error updating ticket status for ${ticketId}:`, error);
    throw error;
  }
};

/**
 * Reabre um ticket fechado (status terminal) e define o novo status.
 * POST /api/private/ticket/{id}/reopen
 */
export const reopenTicket = async (ticketId, status) => {
  try {
    const response = await api.post(`/api/private/ticket/${ticketId}/reopen`, { status });
    return response.data;
  } catch (error) {
    console.error(`Error reopening ticket ${ticketId}:`, error);
    throw error;
  }
};

// Atualização centralizada de responsável(ais), prioridade e appOrigin
export const updateTicketFields = async (ticketId, { responsibleUser, responsibleUsers, priority, appOrigin, companyName }) => {
  let updatedTicket = null;

  // Atualiza lista de responsáveis se fornecida
  if (responsibleUsers !== undefined) {
    updatedTicket = await updateResponsibleUsers(ticketId, responsibleUsers);
  } else if (responsibleUser !== undefined) {
    updatedTicket = await updateResponsibleUser(ticketId, responsibleUser);
  }

  // Atualiza prioridade se fornecido
  if (priority !== undefined) {
    const priorityFn = {
      LOW: lowTicket,
      MEDIUM: mediumTicket,
      HIGH: highTicket,
      CRITICAL: criticalTicket,
    }[priority];
    if (priorityFn) {
      updatedTicket = await priorityFn(ticketId);
    }
  }

  // Atualiza appOrigin e companyName se fornecidos
  const ticketUpdates = {};
  let performUpdate = false;
  if (appOrigin !== undefined) {
    ticketUpdates.appOrigin = appOrigin;
    performUpdate = true;
  }
  if (companyName !== undefined) {
    ticketUpdates.companyName = companyName;
    performUpdate = true;
  }

  if (performUpdate) {
    updatedTicket = await updateTicket(ticketId, ticketUpdates);
  }

  // Busca o ticket atualizado ao final
  if (updatedTicket) {
    return await getTicketById(ticketId);
  }
  return null;
};

export const getAppOrigins = async () => {
  try {
    const response = await api.get('/api/private/ticket/app-origins');
    return response.data;
  } catch (error) {
    console.error('Error fetching app origins:', error);
    throw error;
  }
};

export const getTvDashboard = async (periodDays = 30, requestConfig = {}) => {
  try {
    const params = new URLSearchParams();
    if (periodDays) {
      params.append('periodDays', String(periodDays));
    }
    const response = await api.get('/api/private/ticket/tv-dashboard', {
      params,
      ...requestConfig,
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching TV dashboard:', error);
    throw error;
  }
};

export const getTickeImages = async (ticketId) => {
  try {
    const response = await api.get(`/api/private/ticket/image/${ticketId}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching images for ticket ${ticketId}:`, error);
    throw error;
  }
}